import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { useDeactivateCartMutation, useGetProcessingOrderByUserQuery } from '../../apis/OrderAPi';
import { getUserData } from '../../auth';
import Tooltip from '@mui/material/Tooltip';
import { DoneAllRounded } from '@mui/icons-material';
import TablePagination from '@mui/material/TablePagination';
import { CircularProgress } from '@mui/material';
import _ from "lodash";
import { useNavigate } from 'react-router-dom';

import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField'; // Import TextField for search input


function Row(props) {
  const { row } = props;
  const [open, setOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState('');
  const [deactivateCart, { isLoading, isSuccess, isError }] = useDeactivateCartMutation();
  const navigate = useNavigate();

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setModalOpen(true);
  };

  const handleDeactivate = async () => {
    try {
      await deactivateCart(row.id).unwrap();
      alert('Order Complete successfully!');
      navigate("/customer-orders");
      window.location.reload();
    } catch (error) {
      console.error('Failed to deactivate cart:', error);
      alert('Error Completing Order.');
    }
  };

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
          <TableCell align="right">{row.totalPrice}</TableCell>
         <TableCell>
        {row.status === "processing" && (
            <Tooltip title="Complete Order" placement="top">
              <IconButton 
                onClick={handleDeactivate} 
                color="primary" 
                disabled={isLoading} // Disable button while loading
              >
                {isLoading ? (
                  <CircularProgress
                    size={24} 
                    color="inherit" 
                    style={{ margin: "4px" }} // Adjust the size and position of the spinner
                  />
                ) : (
                  <DoneAllRounded />
                )}
              </IconButton>
            </Tooltip>
          )}

        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">Order Details</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body1"><strong>Code:</strong> {row.orderNumber}</Typography>
                <Typography variant="body1"><strong>Address:</strong> {row.streetName}, {row.apartment}, {row.city}, {row.postCode}</Typography>
                <Typography variant="body1"><strong>Phone:</strong> {row.phone}</Typography>
                <Typography variant="body1"><strong>Email:</strong> {row.email}</Typography>
                <Typography variant="body1"><strong>Order Date:</strong> {new Date(row.orderDate).toLocaleString()}</Typography>
              </Box>
              <Typography variant="h6" gutterBottom component="div" sx={{ mt: 2 }}>Products</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {row.items.map((item) => (
                  <Box key={item.id} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2 }}>
                    <Typography variant="body1"><strong>Shop:</strong> {item.shopName}</Typography>
                    <Typography variant="body1"><strong>Product Name:</strong> {item.productName}</Typography>
                    <Typography variant="body1"><strong>Description:</strong> {item.description}</Typography>
                    <Typography variant="body1"><strong>Price:</strong> {item.discount}</Typography>
                    <Typography variant="body1"><strong>Quantity:</strong> {item.quantity}</Typography>
                    <Typography variant="body1"><strong>Total:</strong> {item.total}</Typography>
                   
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', marginTop: 1 }}>
            {item.images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Item ${index}`}
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  cursor: 'pointer',
                }}
                onClick={() => handleImageClick(image)}
              />
            ))}
          </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
        <DialogContent>
          <img
            src={selectedImage}
            alt="Preview"
            style={{ width: '100%', height: 'auto', maxWidth: '600px' }}
          />
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    orderNumber: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    totalPrice: PropTypes.number.isRequired,
     status: PropTypes.string.isRequired,
    streetName: PropTypes.string.isRequired,
    apartment: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    postCode: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    orderDate: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        productName: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        quantity: PropTypes.number.isRequired,
        total: PropTypes.number.isRequired,
        images: PropTypes.arrayOf(PropTypes.string).isRequired,
      })
    ).isRequired,
  }).isRequired,
};
export default function CollapsibleTable() {
  const userData = getUserData();
  const userId = userData?.uid || "";
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [orderNumber, setOrderNumber] = React.useState('');

  // Added isFetching for background loading states
  const { data, error, isLoading, isFetching } = useGetProcessingOrderByUserQuery({
    userId,
    page,
    size: rowsPerPage,
    orderNumber,
  }
  ,
  { pollingInterval: 5000 } // Poll every 5000ms (5 seconds)
  );
  const sortedCarts = data?.carts ? [...data?.carts].sort((a, b) => b.id - a.id) : [];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page when changing rows per page
  };

  const handleSearchChange = (event) => {
    setOrderNumber(event.target.value);
    setPage(0); // Reset to the first page when searching
  };

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  );  if (error) return <div>Error: {error.message}</div>;

  return (
    <TableContainer component={Paper}>
      <Box sx={{ overflowX: 'auto', padding: 2 }}>
        {/* Search Field */}
        <Box sx={{ padding: 2 }}>
        <TextField
          fullWidth
          label="Search by Order Number"
          variant="outlined"
          value={orderNumber}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: isFetching ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="right">Total</TableCell>
              <TableCell>Complete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCarts?.map((row) => (
              <Row key={row.id} row={row} />
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={data.totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
}